import { QueryClient } from '@tanstack/react-query';

import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';

import { GetRelatedQuestionQuery, QuestionAnsweredStatus } from '@/types/bff/gql-schema';
import { RelatedQnaVo } from '@/types/qna';
import { EQuestionAnsweredStatus } from '@/types/search';
import { absoluteDate, getQnaAnswerStatus, sortImageBlindTypeFormat } from '@/utils/format';
import { useGetRelatedQuestionQuery } from './mobile/qna.gql.impl';

const RELATED_QNA_SIZE = 3;

const questionAnsweredStatusEnumParser = (questionAnsweredStatus: QuestionAnsweredStatus): EQuestionAnsweredStatus => {
  switch (questionAnsweredStatus) {
    case QuestionAnsweredStatus.Answered:
      return EQuestionAnsweredStatus.Answered;
    case QuestionAnsweredStatus.Answering:
      return EQuestionAnsweredStatus.Answering;
    case QuestionAnsweredStatus.Checking:
      return EQuestionAnsweredStatus.Checking;
  }
};

const parseRelatedQnaVo = (qnaDetail: GetRelatedQuestionQuery): RelatedQnaVo[] => {
  if (qnaDetail) {
    return qnaDetail.similarSearchQuestionCardCursorPageRequest.elements.map((element) => {
      return {
        ...element,
        question: {
          ...element.question,
          attachments: sortImageBlindTypeFormat(element.question.attachments),
        },
        answeredStatus: questionAnsweredStatusEnumParser(element.answeredStatus),
        answeredStatusText: getQnaAnswerStatus(element.answeredStatus),
        questionCreatedAt: absoluteDate(element.question.createdAt),
      };
    });
  }
  return [] as RelatedQnaVo[];
};

const useGetRelatedQna = (questionPid: string) => {
  const { data: relatedQna, isLoading } = useGetRelatedQuestionQuery(
    {
      similarSearchQuestionInput: {
        questionPid,
        size: RELATED_QNA_SIZE,
      },
    },
    {
      select: parseRelatedQnaVo,
      staleTime: SIX_HOURS_IN_MILLISECONDS,
      cacheTime: SIX_HOURS_IN_MILLISECONDS,
      enabled: !!questionPid,
    }
  );
  return {
    relatedQna,
    isLoading,
  };
};

export default useGetRelatedQna;

useGetRelatedQna.getKey = (questionPid: string) => {
  return useGetRelatedQuestionQuery.getKey({
    similarSearchQuestionInput: {
      questionPid,
      size: RELATED_QNA_SIZE,
    },
  });
};

useGetRelatedQna.prefetchQuery = async (queryClient: QueryClient, questionPid: string) => {
  await queryClient.prefetchQuery(
    useGetRelatedQuestionQuery.getKey({
      similarSearchQuestionInput: {
        questionPid,
        size: RELATED_QNA_SIZE,
      },
    }),
    useGetRelatedQuestionQuery.fetcher({
      similarSearchQuestionInput: {
        questionPid,
        size: RELATED_QNA_SIZE,
      },
    })
  );
};
